import { getMessageBehavior } from './behavior';
import { MESSAGE_DATA_TYPES } from './discriminators';
import { isSupportMessageVisibleTo, type SupportMessageAudience } from './supportMessage';

export type { MessageBehavior } from './base';
export { getMessageBehavior } from './behavior';

/**
 * Shared reads over a Twilio message's raw attributes.
 *
 * The inbox handlers and the parse path both need to know a message's type and
 * fields before anything is validated, and each used to re-implement the
 * nesting flatten below. Centralizing it here is what lets a type declare its
 * inbox behavior once instead of being special-cased per handler.
 */

/**
 * Flattens the `data` nesting into a single attribute bag.
 *
 * A message can carry its payload nested under `data` (what the client sends),
 * mirrored onto the top level (what the backend writes), or both. Merging with
 * `data` last matches the parse path.
 *
 * Precedence is unobservable in practice and must stay that way: `data` holds a
 * `MessageData` payload, whose `type` is exactly what the backend mirrors to
 * the top level, so the two can never disagree. Fields absent from that union —
 * `draft` is the one that matters — only ever exist at the top level and so
 * survive the merge untouched. A future producer that writes a *conflicting*
 * top-level value would break that invariant and change what every caller here
 * reads.
 */
export function normalizeMessageAttributes(
  attributes: unknown
): Record<string, unknown> | undefined {
  if (!attributes || typeof attributes !== 'object') {
    return undefined;
  }

  const rawAttributes = attributes as Record<string, unknown>;

  if (rawAttributes.data && typeof rawAttributes.data === 'object') {
    return { ...rawAttributes, ...(rawAttributes.data as Record<string, unknown>) };
  }

  return rawAttributes;
}

/** Reads a message's type discriminator without validating the payload. */
export function readMessageType(attributes: unknown): string | undefined {
  const normalized = normalizeMessageAttributes(attributes);

  return typeof normalized?.type === 'string' ? normalized.type : undefined;
}

/** True when the message is flagged as a hidden draft. */
export function readIsDraft(attributes: unknown): boolean {
  return normalizeMessageAttributes(attributes)?.draft === true;
}

/**
 * Whether a support message's audience includes this viewer, read straight off
 * raw attributes.
 *
 * The inbox handlers see raw Twilio attributes rather than a parsed message, so
 * they need this alongside `readIsDraft`; the chat list gates the same messages
 * through `isSupportMessageVisibleTo` on the parsed shape. Both call the one
 * predicate over the same set of messages — only the support type — so a
 * message cannot be hidden from one layer and shown in the other.
 *
 * `isSubmitter` is not available here: the inbox handlers hold a conversation,
 * not an inquiry role, so only the resolved `audienceUserIds` can answer. A
 * message carrying an `audience` label but no ids therefore passes here and is
 * gated in the chat list alone. It leaves that viewer an inbox preview for a
 * message their thread will not show, which is the fail-open side of a
 * cosmetic gate; the backend writes both fields together, so it is a shape we
 * tolerate rather than one we expect.
 */
export function isMessageForViewer(
  attributes: unknown,
  viewerUserId: string | number | undefined
): boolean {
  const normalized = normalizeMessageAttributes(attributes);

  if (normalized?.type !== MESSAGE_DATA_TYPES.SUPPORT_MESSAGE) {
    return true;
  }

  return isSupportMessageVisibleTo(
    {
      audience: normalized.audience as SupportMessageAudience | undefined,
      audienceUserIds: Array.isArray(normalized.audienceUserIds)
        ? (normalized.audienceUserIds as number[])
        : undefined,
    },
    { userId: viewerUserId }
  );
}

/**
 * Whether a message type drives the optimistic inbox unread badge and preview.
 * Both inbox handlers gate on this, so suppressing a new type is a one-line
 * `behavior` export on its module rather than an edit to each handler.
 */
export function drivesInboxPreview(type: string | undefined): boolean {
  return getMessageBehavior(type).drivesInboxPreview;
}
